import React, { useEffect, useState, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InstructionalText from '@components/ssap/notifications/InstructionalText';

import { DATE_FORMAT_DASHED } from '@utils/dates/dateMethods';
import { DIGIT_ONLY_REGEX } from '@utils/validators/regex';
import {
    constructDate,
    convertDateToUtc,
    formatDate,
    getDateTimeInMillisecond,
    parseDate,
} from '@utils/dates/date.utils';

import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';

const DATE = 'date';
const MONTH = 'month';
const YEAR = 'year';

const EMPTY_DATE = {
    [DATE]: '',
    [MONTH]: '',
    [YEAR]: '',
};

const MIN_VALID_DATE = '1900-01-01';
const MIN_EPOCH_MS = -62135654400000;

const DatePicker = props => {
    const dayInputRef = useRef(null);
    const yearInputRef = useRef(null);

    const {
        inputData: {
            id,
            isInputRequired,
            name,
            value,
            divClass,
            specificValidation,
        },
        indexInArray,
        containerClass,
        label: {
            dataInstructions,
            dataInstructionsAnchor: anchor,
            onlyInstructions,
            replacements,
        },
        inputActions: { handleFieldChange },
    } = props;

    const intl = useIntl();
    const yearPlaceholder = intl.formatMessage({ id: 'common.abbr.year' });

    const [fullDate, updateFullDate] = useState(EMPTY_DATE);
    const [focusTarget, setFocusTarget] = useState(null);

    const date = fullDate[DATE];
    const month = fullDate[MONTH];
    const year = fullDate[YEAR];

    useEffect(() => {
        if (value === DATE_FORMAT_DASHED) {
            return;
        }
        if (value === null || value === '') {
            updateFullDate({ ...EMPTY_DATE });
            return;
        }

        let timeValue;
        switch (typeof value) {
            case 'string': // from user typing into DatePicker
                timeValue = parseDate(value);
                break;
            case 'number': // from anonymous shopping (old jsp)
                timeValue = convertDateToUtc(value);
                break;
            default:
                return;
        }

        updateFullDate({
            [DATE]: formatDate(timeValue, 'DD'),
            [MONTH]: formatDate(timeValue, 'MM'),
            [YEAR]: formatDate(timeValue, 'YYYY'),
        });
    }, [value]);

    useEffect(() => {
        if (focusTarget === 'day') dayInputRef.current?.focus();
        if (focusTarget === 'year') yearInputRef.current?.focus();
        setFocusTarget(null);
    }, [focusTarget]);

    const handleValueChange = e => {
        const { name } = e.target;
        const value = e.target.value.replace(DIGIT_ONLY_REGEX, '');

        switch (name) {
            case MONTH:
                if (+value > 12 || +value < 0 || value.length > 2) {
                    return null;
                }
                break;
            case DATE:
                if (value > 31 || value < 0 || value.length > 2) {
                    return null;
                }
                break;
            case YEAR:
                if (value.length > 4) {
                    return null;
                }
                break;
        }

        updateFullDate(date => ({
            ...date,
            [ name ]: value
        }));

        if (
            name === MONTH &&
            value.length === VALIDATION_TYPES.MONTH_AND_DAY_LENGTH &&
            document.activeElement !== dayInputRef.current
        ) {
            setFocusTarget('day');
        }
        if (
            name === DATE &&
            value.length === VALIDATION_TYPES.MONTH_AND_DAY_LENGTH &&
            document.activeElement !== yearInputRef.current
        ) {
            setFocusTarget('year');
        }
    };

    const handleBlur = () => {
        const constructedDate = constructDate({
            [DATE]: +date,
            [MONTH]: +month - 1,
            [YEAR]: +year,
        });
        const isCompletelyEmpty = Object.values(fullDate).every(part => part === '');
        const allPartsFilled = Object.values(fullDate).every(part => part !== '');
        const isBelowMinDate =
            isNaN(getDateTimeInMillisecond(constructedDate)) ||
            getDateTimeInMillisecond(constructedDate) < getDateTimeInMillisecond(MIN_VALID_DATE);

        // Complete but invalid calendar dates (e.g. Feb 30, Apr 31) keeps the typed digits
        // visible and stores DATE_FORMAT_DASHED so Save & Continue shows the correct-date error.
        // specificValidation uses the same sentinel for any non-empty invalid/below-min input.
        if (!isCompletelyEmpty && (specificValidation || allPartsFilled) && isBelowMinDate) {
            handleFieldChange(DATE_FORMAT_DASHED);
            return;
        }

        const isInvalid =
            !constructedDate ||
            isNaN(constructedDate.toDate().getTime()) ||
            constructedDate.toDate().getTime() < MIN_EPOCH_MS;

        handleFieldChange(isInvalid ? null : constructedDate.format(DATE_FORMAT_DASHED));
    };

    const legendDataTestId = `${id}_legend`;

    return (
        <div className={`grid-row gi-component gi-datepicker ${containerClass}`}>
            <div className="grid-col-12">
                <fieldset className="usa-fieldset">
                    <legend
                        data-testid={legendDataTestId}
                        className={`usa-legend gi-datepicker__label ${props.label.labelClass}`}
                    >
                        <span
                            className={`required-${isInputRequired}`}
                            data-testid={`${legendDataTestId}_span`}
                        >
                            {props.label.text}
                        </span>
                        {dataInstructions && (
                            <InstructionalText
                                anchor={anchor}
                                onlyInstructions={onlyInstructions}
                                Component={<span data-instructions={dataInstructions} />}
                                replacements={replacements}
                            />
                        )}
                    </legend>

                    <span className={`grid-row usa-memorable-date ${divClass}`}>
                        <span className="usa-form-group usa-form-group--month gi-form-group_month txt-left">
                            <label
                                data-testid={`${id}_month_label`}
                                htmlFor={`hhm${indexInArray}_${name}_1`}
                            >
                                <FormattedMessage id="common.month" />
                            </label>
                            <input
                                id={`hhm${indexInArray}_${name}_1`}
                                data-testid={`${id}_month_input`}
                                name={MONTH}
                                maxLength={VALIDATION_TYPES.MONTH_AND_DAY_LENGTH}
                                value={month}
                                placeholder="MM"
                                onChange={handleValueChange}
                                onBlur={handleBlur}
                                className="usa-input usa-input--inline gi-datepicker_input"
                            />
                        </span>
                        <span className="usa-form-group usa-form-group--day gi-form-group_day txt-left">
                            <label
                                data-testid={`${id}_day_label`}
                                htmlFor={`hhm${indexInArray}_${name}_2`}
                            >
                                <FormattedMessage id="common.day" />
                            </label>
                            <input
                                id={`hhm${indexInArray}_${name}_2`}
                                data-testid={`${id}_day_input`}
                                ref={dayInputRef}
                                name={DATE}
                                maxLength={VALIDATION_TYPES.MONTH_AND_DAY_LENGTH}
                                value={date}
                                placeholder="DD"
                                onChange={handleValueChange}
                                onBlur={handleBlur}
                                className="usa-input usa-input--inline gi-datepicker_input"
                            />
                        </span>
                        <span className="usa-form-group usa-form-group--year gi-form-group_year txt-left">
                            <label
                                data-testid={`${id}_year_label`}
                                htmlFor={`hhm${indexInArray}_${name}_3`}
                            >
                                <FormattedMessage id="common.year" />
                            </label>
                            <input
                                id={`hhm${indexInArray}_${name}_3`}
                                data-testid={`${id}_year_input`}
                                ref={yearInputRef}
                                name={YEAR}
                                maxLength={VALIDATION_TYPES.YEAR_LENGTH}
                                value={year}
                                placeholder={yearPlaceholder}
                                onChange={handleValueChange}
                                onBlur={handleBlur}
                                className="usa-input usa-input--inline gi-datepicker_input"
                            />
                        </span>
                    </span>
                </fieldset>
            </div>
        </div>
    );
};

export default DatePicker;
